import { useEffect, useState } from 'react';
import styles from './Listings.module.css';
import Button from '../../components/Button/Button';
import Modal, { ModalTitle } from '../../components/Modal/Modal';
import { api, uploadImage } from '../../lib/api';
import { unitsFor } from '../../lib/units';

const CATEGORIES = [
    ['produce', 'Produce'],
    ['livestock', 'Livestock'],
    ['equipment', 'Equipment'],
];

// Blank inputs for unset numbers rather than "null" / "0".
const asInput = (value) => (value == null ? '' : String(value));

// Edits a seller's listing in place via PATCH /api/admin/listings/:id — live
// immediately, no points charged, and the seller is notified. Location isn't
// editable here (see the route for why).
const EditListingModal = ({ item, onClose, onSaved }) => {
    const [form, setForm] = useState({
        name: item.name || '',
        description: item.description,
        category: item.categoryKey,
        unit: item.unit || unitsFor(item.categoryKey)[0],
        stockKg: asInput(item.stock),
        retailPrice: asInput(item.retailPrice),
        bulkPrice: asInput(item.bulkPrice),
        bulkMin: asInput(item.bulkMin),
        pickup: item.pickup,
        pickupInstructions: item.pickupInstructions,
        delivery: item.delivery,
        deliveryFee: asInput(item.deliveryFee),
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    // A replacement photo, held locally until Save — uploading on pick would
    // leave an orphaned file in storage every time an admin cancels.
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);

    useEffect(() => {
        if (!photoFile) {
            setPhotoPreview(null);
            return undefined;
        }
        const url = URL.createObjectURL(photoFile);
        setPhotoPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [photoFile]);

    const pickPhoto = (event) => {
        const file = event.target.files?.[0];
        event.target.value = ''; // so re-picking the same file still fires
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setError('Please choose an image file.');
            return;
        }
        setError('');
        setPhotoFile(file);
    };

    const set = (key) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    // Switching category keeps the unit if the new category offers it, and
    // otherwise falls back to that category's first unit — as the app does.
    const setCategory = (event) => {
        const category = event.target.value;
        setForm((prev) => {
            const options = unitsFor(category);
            return { ...prev, category, unit: options.includes(prev.unit) ? prev.unit : options[0] };
        });
    };

    // A listing stored with a unit no longer on the list (e.g. a legacy hire
    // unit) keeps it as an option, so opening the form doesn't silently change it.
    const unitOptions = unitsFor(form.category);
    if (form.unit && !unitOptions.includes(form.unit)) unitOptions.unshift(form.unit);

    const canSave =
        form.name.trim() && Number(form.retailPrice) > 0 && form.stockKg !== '' && Number(form.stockKg) >= 0;

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            // Only sent when replaced: `item.image` may be a display fallback
            // (the category's default photo), not something to store.
            const image = photoFile ? await uploadImage(photoFile) : undefined;
            await api(`/admin/listings/${item._id}`, {
                method: 'PATCH',
                body: {
                    image,
                    name: form.name.trim(),
                    description: form.description.trim(),
                    category: form.category,
                    unit: form.unit,
                    stockKg: Number(form.stockKg),
                    retailPrice: Number(form.retailPrice),
                    // Blank clears bulk pricing / the delivery quote.
                    bulkPrice: form.bulkPrice === '' ? null : Number(form.bulkPrice),
                    bulkMin: form.bulkMin === '' ? null : Number(form.bulkMin),
                    pickup: form.pickup,
                    pickupInstructions: form.pickupInstructions.trim(),
                    delivery: form.delivery,
                    deliveryFee: form.deliveryFee === '' ? null : Number(form.deliveryFee),
                },
            });
            await onSaved();
        } catch (err) {
            setError(err.message);
            setSaving(false);
        }
    };

    const field = (id, label, input) => (
        <div className="mt-3">
            <label htmlFor={id} className={`${styles.inputLabel} mb-2`}>{label}</label>
            {input}
        </div>
    );

    return (
        <Modal onClose={onClose}>
            <ModalTitle>Edit listing</ModalTitle>
            <div className={styles.seller}>{item.seller}</div>

            <div className="d-flex align-items-center gap-3 mt-3">
                <div className={styles.modalThumb}>
                    <img src={photoPreview || item.image} alt={form.name || 'Listing'} />
                </div>
                <div className="d-flex flex-column align-items-start gap-1">
                    <label htmlFor="edit-photo" className={styles.photoPick}>
                        {photoFile ? 'Choose a different photo' : 'Change photo'}
                    </label>
                    <input id="edit-photo" type="file" accept="image/*" hidden onChange={pickPhoto} />
                    {photoFile && (
                        <button type="button" className={styles.photoUndo} onClick={() => setPhotoFile(null)}>
                            Keep current photo
                        </button>
                    )}
                </div>
            </div>

            {field('edit-name', 'Name*', (
                <input id="edit-name" className={`${styles.qtyInput} w-100`} value={form.name} onChange={set('name')} />
            ))}
            {field('edit-description', 'Description', (
                <textarea
                    id="edit-description"
                    rows={3}
                    className={`${styles.qtyInput} w-100`}
                    value={form.description}
                    onChange={set('description')}
                />
            ))}

            <div className="d-flex gap-3">
                <div className="flex-fill">
                    {field('edit-category', 'Category', (
                        <select id="edit-category" className={`${styles.qtyInput} w-100`} value={form.category} onChange={setCategory}>
                            {CATEGORIES.map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                    ))}
                </div>
                <div className="flex-fill">
                    {field('edit-unit', 'Unit', (
                        <select id="edit-unit" className={`${styles.qtyInput} w-100`} value={form.unit} onChange={set('unit')}>
                            {unitOptions.map((unit) => (
                                <option key={unit} value={unit}>{unit}</option>
                            ))}
                        </select>
                    ))}
                </div>
            </div>

            <div className="d-flex gap-3">
                <div className="flex-fill">
                    {field('edit-price', 'Retail price (K)*', (
                        <input id="edit-price" type="number" min="0" className={`${styles.qtyInput} w-100`} value={form.retailPrice} onChange={set('retailPrice')} />
                    ))}
                </div>
                <div className="flex-fill">
                    {field('edit-stock', 'In stock*', (
                        <input id="edit-stock" type="number" min="0" className={`${styles.qtyInput} w-100`} value={form.stockKg} onChange={set('stockKg')} />
                    ))}
                </div>
            </div>

            <div className="d-flex gap-3">
                <div className="flex-fill">
                    {field('edit-bulk-price', 'Bulk price (K)', (
                        <input id="edit-bulk-price" type="number" min="0" placeholder="None" className={`${styles.qtyInput} w-100`} value={form.bulkPrice} onChange={set('bulkPrice')} />
                    ))}
                </div>
                <div className="flex-fill">
                    {field('edit-bulk-min', 'Bulk minimum', (
                        <input id="edit-bulk-min" type="number" min="0" placeholder="None" className={`${styles.qtyInput} w-100`} value={form.bulkMin} onChange={set('bulkMin')} />
                    ))}
                </div>
            </div>

            <div className="d-flex gap-4 mt-3">
                <label className="d-flex align-items-center gap-2">
                    <input type="checkbox" checked={form.pickup} onChange={set('pickup')} /> Pickup
                </label>
                <label className="d-flex align-items-center gap-2">
                    <input type="checkbox" checked={form.delivery} onChange={set('delivery')} /> Delivery
                </label>
            </div>
            {form.pickup && field('edit-pickup', 'Pickup instructions', (
                <input id="edit-pickup" className={`${styles.qtyInput} w-100`} value={form.pickupInstructions} onChange={set('pickupInstructions')} />
            ))}
            {form.delivery && field('edit-delivery-fee', 'Delivery fee (K)', (
                <input
                    id="edit-delivery-fee"
                    type="number"
                    min="0"
                    placeholder="Agreed with buyer"
                    className={`${styles.qtyInput} w-100`}
                    value={form.deliveryFee}
                    onChange={set('deliveryFee')}
                />
            ))}

            <div className={`${styles.restockNote} p-3 mt-4`}>
                Changes go live immediately, no points are charged, and the seller is notified.
            </div>
            {error && <div className="text-danger mt-3">{error}</div>}

            <div className="d-flex gap-3 mt-4">
                <Button variant="outline" className="flex-fill" onClick={onClose}>
                    Cancel
                </Button>
                <Button className="flex-fill" disabled={!canSave || saving} onClick={handleSave}>
                    {saving ? (photoFile ? 'Uploading…' : 'Saving…') : 'Save changes'}
                </Button>
            </div>
        </Modal>
    );
};

export default EditListingModal;
